/**
 * DbCheckbox - 复选框组件
 *
 * 特性：
 * - 支持左右布局（左侧选择框右侧文字 / 左侧文字右侧选择框）
 * - 整行可点击，提升用户体验
 * - 支持描述文字
 * - 支持不确定状态（半选）
 * - 支持自定义选中/未选中/不确定状态图标
 * - 支持受控和非受控模式
 * - 支持 CheckboxGroup 多选
 * - 支持自定义 Checkbox 样式（大小、边框、颜色等）
 * - 支持自定义 label 样式和内容
 */

import React, { useState, useCallback, useMemo, createContext, useContext } from 'react';
import { View, Text, TouchableOpacity, ViewStyle } from 'react-native';
import {
    DbCheckboxProps,
    DbCheckboxGroupProps,
    DbCheckboxItemProps,
    CheckboxOption,
    CheckboxLayout,
    CheckboxStyleConfig,
} from './DbCheckbox.types';
import { createStyles, getSizeConfig } from './DbCheckbox.styles';
import DbIcon from '../DbIcon';

// ==================== Context ====================

interface CheckboxGroupContextValue {
    value?: any[];
    onChange?: (value: any, checked: boolean) => void;
    layout?: CheckboxLayout;
    size?: 'small' | 'medium' | 'large';
    activeColor?: string;
    inactiveColor?: string;
    checkboxConfig?: CheckboxStyleConfig;
    disabled?: boolean;
    labelStyle?: any;
    descriptionStyle?: any;
    labelColor?: string;
    descriptionColor?: string;
    renderChecked?: () => React.ReactNode;
    renderUnchecked?: () => React.ReactNode;
}

const CheckboxGroupContext = createContext<CheckboxGroupContextValue>({});

// ==================== 单个 Checkbox 组件 ====================

export const DbCheckbox: React.FC<DbCheckboxProps> & {
    Group: typeof DbCheckboxGroup;
    Item: typeof DbCheckboxItem;
} = ({
    checked: checkedProp,
    defaultChecked = false,
    onChange,
    label,
    labelContent,
    disabled = false,
    description,
    descriptionContent,
    layout = 'left',
    size = 'medium',
    activeColor = '#007AFF',
    inactiveColor,
    checkboxConfig,
    indeterminate = false,
    style,
    labelStyle,
    descriptionStyle,
    labelColor,
    descriptionColor,
    renderChecked,
    renderUnchecked,
    renderIndeterminate,
}) => {
    // 非受控模式的内部状态
    const [internalChecked, setInternalChecked] = useState(defaultChecked);
    
    // 最终的选中状态
    const isChecked = checkedProp !== undefined ? checkedProp : internalChecked;

    const styles = useMemo(
        () => createStyles(size, activeColor),
        [size, activeColor]
    );

    const sizeConfig = useMemo(() => getSizeConfig(size), [size]);

    const handlePress = useCallback(() => {
        if (disabled) return;
        
        const newChecked = !isChecked;
        
        if (checkedProp === undefined) {
            setInternalChecked(newChecked);
        }
        onChange?.(newChecked);
    }, [disabled, isChecked, checkedProp, onChange]);

    // 计算 Checkbox 样式
    const getCheckboxStyle = (isActive: boolean) => {
        const boxSize = checkboxConfig?.boxSize ?? sizeConfig.boxSize;
        const borderWidth = checkboxConfig?.borderWidth ?? 2;
        const borderRadius = checkboxConfig?.borderRadius ?? sizeConfig.borderRadius;
        const borderColor = isActive 
            ? (checkboxConfig?.activeBorderColor ?? activeColor)
            : (checkboxConfig?.borderColor ?? inactiveColor ?? '#D1D1D6');
        const backgroundColor = isActive 
            ? (checkboxConfig?.activeBackgroundColor ?? activeColor)
            : 'transparent';

        return {
            width: boxSize,
            height: boxSize,
            borderRadius,
            borderWidth,
            borderColor: disabled ? '#E5E5EA' : borderColor,
            backgroundColor: disabled ? (isActive ? '#E5E5EA' : 'transparent') : backgroundColor,
        };
    };

    // 渲染 Checkbox 方框
    const renderCheckboxBox = () => {
        const iconSize = checkboxConfig?.iconSize ?? sizeConfig.iconSize;
        const checkColor = checkboxConfig?.checkColor ?? '#FFFFFF';

        // 不确定状态
        if (indeterminate) {
            if (renderIndeterminate) {
                return renderIndeterminate();
            }
            return (
                <View
                    style={[
                        styles.checkbox,
                        getCheckboxStyle(true),
                        checkboxConfig?.boxStyle,
                    ]}
                >
                    <View style={[styles.indeterminateLine, { backgroundColor: checkColor }]} />
                </View>
            );
        }

        // 选中状态
        if (isChecked) {
            if (renderChecked) {
                return renderChecked();
            }
            return (
                <View
                    style={[
                        styles.checkbox,
                        getCheckboxStyle(true),
                        checkboxConfig?.boxStyle,
                    ]}
                >
                    <DbIcon
                        name="checkmark"
                        family="Ionicons"
                        size={iconSize}
                        color={checkColor}
                    />
                </View>
            );
        }

        // 未选中状态
        if (renderUnchecked) {
            return renderUnchecked();
        }
        return (
            <View
                style={[
                    styles.checkbox,
                    getCheckboxStyle(false),
                    checkboxConfig?.boxStyle,
                ]}
            />
        );
    };

    // 渲染文字内容
    const renderTextContent = () => {
        const hasLabel = label || labelContent;
        const hasDescription = description || descriptionContent;
        
        if (!hasLabel && !hasDescription) return null;

        return (
            <View
                style={[
                    styles.textContainer,
                    layout === 'left' ? styles.textContainerLeft : styles.textContainerRight,
                ]}
            >
                {/* 标签内容 */}
                {labelContent ? (
                    labelContent
                ) : label ? (
                    <Text
                        style={[
                            styles.label,
                            labelColor && { color: labelColor },
                            disabled && styles.labelDisabled,
                            labelStyle,
                        ]}
                        numberOfLines={1}
                    >
                        {label}
                    </Text>
                ) : null}
                
                {/* 描述内容 */}
                {descriptionContent ? (
                    descriptionContent
                ) : description ? (
                    <Text
                        style={[
                            styles.description,
                            descriptionColor && { color: descriptionColor },
                            disabled && styles.descriptionDisabled,
                            descriptionStyle,
                        ]}
                        numberOfLines={2}
                    >
                        {description}
                    </Text>
                ) : null}
            </View>
        );
    };

    return (
        <TouchableOpacity
            style={[
                styles.itemContainer,
                layout === 'left' ? styles.itemContainerLeft : styles.itemContainerRight,
                disabled && styles.itemContainerDisabled,
                style,
            ]}
            onPress={handlePress}
            disabled={disabled}
            activeOpacity={0.6}
        >
            {renderCheckboxBox()}
            {renderTextContent()}
        </TouchableOpacity>
    );
};

// ==================== CheckboxItem 组件（用于 Group 内） ====================

export const DbCheckboxItem = <T extends any = string>({
    value,
    label,
    labelContent,
    disabled = false,
    description,
    descriptionContent,
    style,
    labelStyle,
    descriptionStyle,
    labelColor,
    descriptionColor,
}: DbCheckboxItemProps<T>) => {
    // 从 Context 获取配置
    const context = useContext(CheckboxGroupContext);
    
    // 判断是否选中
    const isChecked = context.value?.includes(value) ?? false;
    const finalDisabled = context.disabled || disabled;
    const finalLabelStyle = context.labelStyle ?? labelStyle;
    const finalDescriptionStyle = context.descriptionStyle ?? descriptionStyle;
    const finalLabelColor = context.labelColor ?? labelColor;
    const finalDescriptionColor = context.descriptionColor ?? descriptionColor;

    const styles = useMemo(
        () => createStyles(context.size || 'medium', context.activeColor || '#007AFF'),
        [context.size, context.activeColor]
    );

    const sizeConfig = useMemo(() => getSizeConfig(context.size || 'medium'), [context.size]);

    const handlePress = useCallback(() => {
        if (finalDisabled) return;
        context.onChange?.(value, !isChecked);
    }, [finalDisabled, context, value, isChecked]);

    // 计算 Checkbox 样式
    const getCheckboxStyle = (isActive: boolean) => {
        const config = context.checkboxConfig;
        const boxSize = config?.boxSize ?? sizeConfig.boxSize;
        const borderWidth = config?.borderWidth ?? 2;
        const borderRadius = config?.borderRadius ?? sizeConfig.borderRadius;
        const borderColor = isActive 
            ? (config?.activeBorderColor ?? context.activeColor ?? '#007AFF')
            : (config?.borderColor ?? context.inactiveColor ?? '#D1D1D6');
        const backgroundColor = isActive 
            ? (config?.activeBackgroundColor ?? context.activeColor ?? '#007AFF')
            : 'transparent';

        return {
            width: boxSize,
            height: boxSize,
            borderRadius,
            borderWidth,
            borderColor: finalDisabled ? '#E5E5EA' : borderColor,
            backgroundColor: finalDisabled ? (isActive ? '#E5E5EA' : 'transparent') : backgroundColor,
        };
    };

    // 渲染 Checkbox 方框
    const renderCheckbox = () => {
        const config = context.checkboxConfig;
        const iconSize = config?.iconSize ?? sizeConfig.iconSize;
        const checkColor = config?.checkColor ?? '#FFFFFF';

        if (isChecked) {
            if (context.renderChecked) {
                return context.renderChecked();
            }
            return (
                <View
                    style={[
                        styles.checkbox,
                        getCheckboxStyle(true),
                        config?.boxStyle,
                    ]}
                >
                    <DbIcon
                        name="checkmark"
                        family="Ionicons"
                        size={iconSize}
                        color={checkColor}
                    />
                </View>
            );
        }

        if (context.renderUnchecked) {
            return context.renderUnchecked();
        }
        return (
            <View
                style={[
                    styles.checkbox,
                    getCheckboxStyle(false),
                    config?.boxStyle,
                ]}
            />
        );
    };

    // 渲染文字内容
    const renderTextContent = () => (
        <View
            style={[
                styles.textContainer,
                context.layout === 'left' ? styles.textContainerLeft : styles.textContainerRight,
            ]}
        >
            {/* 标签内容 */}
            {labelContent ? (
                labelContent
            ) : label ? (
                <Text
                    style={[
                        styles.label,
                        finalLabelColor && { color: finalLabelColor },
                        finalDisabled && styles.labelDisabled,
                        finalLabelStyle,
                    ]}
                    numberOfLines={1}
                >
                    {label}
                </Text>
            ) : null}
            
            {/* 描述内容 */}
            {descriptionContent ? (
                descriptionContent
            ) : description ? (
                <Text
                    style={[
                        styles.description,
                        finalDescriptionColor && { color: finalDescriptionColor },
                        finalDisabled && styles.descriptionDisabled,
                        finalDescriptionStyle,
                    ]}
                    numberOfLines={2}
                >
                    {description}
                </Text>
            ) : null}
        </View>
    );

    return (
        <TouchableOpacity
            style={[
                styles.itemContainer,
                context.layout === 'left' ? styles.itemContainerLeft : styles.itemContainerRight,
                finalDisabled && styles.itemContainerDisabled,
                style,
            ]}
            onPress={handlePress}
            disabled={finalDisabled}
            activeOpacity={0.6}
        >
            {renderCheckbox()}
            {renderTextContent()}
        </TouchableOpacity>
    );
};

// ==================== CheckboxGroup 组件 ====================

export const DbCheckboxGroup = <T extends any = string>({
    value: valueProp,
    defaultValue = [],
    onChange,
    options,
    disabled = false,
    layout = 'left',
    size = 'medium',
    activeColor = '#007AFF',
    inactiveColor,
    checkboxConfig,
    min,
    max,
    spacing = 0,
    style,
    itemStyle,
    labelStyle,
    descriptionStyle,
    labelColor,
    descriptionColor,
    renderChecked,
    renderUnchecked,
    children,
}: DbCheckboxGroupProps<T>) => {
    // 非受控模式的内部状态
    const [internalValue, setInternalValue] = useState<T[]>(defaultValue);
    
    // 最终值
    const currentValue = valueProp !== undefined ? valueProp : internalValue;

    const handleChange = useCallback(
        (itemValue: T, checked: boolean) => {
            let newValue: T[];
            
            if (checked) {
                // 检查最大数量限制
                if (max !== undefined && currentValue.length >= max) {
                    return;
                }
                newValue = [...currentValue, itemValue];
            } else {
                // 检查最小数量限制
                if (min !== undefined && currentValue.length <= min) {
                    return;
                }
                newValue = currentValue.filter((v) => v !== itemValue);
            }
            
            if (valueProp === undefined) {
                setInternalValue(newValue);
            }
            onChange?.(newValue);
        },
        [currentValue, valueProp, onChange, min, max]
    );

    const contextValue = useMemo<CheckboxGroupContextValue>(
        () => ({
            value: currentValue,
            onChange: handleChange,
            layout,
            size,
            activeColor,
            inactiveColor,
            checkboxConfig,
            disabled,
            labelStyle,
            descriptionStyle,
            labelColor,
            descriptionColor,
            renderChecked,
            renderUnchecked,
        }),
        [currentValue, handleChange, layout, size, activeColor, inactiveColor, checkboxConfig, disabled, labelStyle, descriptionStyle, labelColor, descriptionColor, renderChecked, renderUnchecked]
    );

    const styles = useMemo(() => createStyles(size, activeColor), [size, activeColor]);

    // 渲染选项列表
    const renderOptions = () => {
        if (options && options.length > 0) {
            return options.map((option, index) => (
                <React.Fragment key={String(option.value)}>
                    <DbCheckboxItem<T>
                        value={option.value}
                        label={option.label}
                        labelContent={option.labelContent}
                        disabled={option.disabled}
                        description={option.description}
                        descriptionContent={option.descriptionContent}
                        style={[
                            index > 0 && spacing > 0 ? { marginTop: spacing } as ViewStyle : undefined,
                            itemStyle,
                        ]}
                    />
                    {index < options.length - 1 && spacing === 0 && (
                        <View style={styles.separator} />
                    )}
                </React.Fragment>
            ));
        }

        // 使用 children
        return children;
    };

    return (
        <CheckboxGroupContext.Provider value={contextValue}>
            <View style={[styles.container, style]}>
                {renderOptions()}
            </View>
        </CheckboxGroupContext.Provider>
    );
};

// 挂载子组件
DbCheckbox.Group = DbCheckboxGroup;
DbCheckbox.Item = DbCheckboxItem;
DbCheckbox.displayName = 'DbCheckbox';

export default DbCheckbox;
export type {
    DbCheckboxProps,
    DbCheckboxGroupProps,
    DbCheckboxItemProps,
    CheckboxOption,
    CheckboxLayout,
    CheckboxStyleConfig,
} from './DbCheckbox.types';
